import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row, Button } from "react-bootstrap";
import InputField from "../../../common/InputFieldWithDropDown/InputField";
import { useMyContext } from "../../../myContext";
import { generateDirectQuote } from "../../../api";
import { addCommasInPrice, decimalAdjust, inputAmount } from "../../../utils";
import indiaflag from "../../../public/assets/inflag.svg";
import styles from "./firstSection.module.css";
import InfoBox from "../../../common/InfoBox/InfoBox";
import arriveIcon from "../../../public/assets/firstsecarrive.svg";
import infoicon from "../../../public/assets/infoicongray.svg";
import downwardicon from "../../../public/assets/downwardicon.svg";
import upwaredicon from "../../../public/assets/upwaredicon.svg";
import SideModal from "../SideModal/SideModal";
import { useNavigate } from "react-router";
import { defaultCurrency } from "../../../constant";

const DEFAULT_VALUE = "--";
const DEBOUNCE_DELAY = 400;

const initialQuoteState = {
  transferFee: DEFAULT_VALUE,
  amountConversion: DEFAULT_VALUE,
  finalAmount: DEFAULT_VALUE,
  tcs: DEFAULT_VALUE,
  gst: DEFAULT_VALUE,
  forexRate: DEFAULT_VALUE,
  totalAdditionalAmount: DEFAULT_VALUE,
};

const DropdownRow = ({ label, value, isBlue = false }) => (
  <>
    <Row>
      <Col
        md={8}
        sm={8}
        xs={8}
        className={isBlue ? styles.dropdownblueCol : styles.dropdownCol}
        style={{ paddingRight: "0px" }}
      >
        {label}
      </Col>
      <Col
        md={4}
        sm={4}
        xs={4}
        className={isBlue ? styles.dropdownblueCol : styles.dropdownCol}
        style={{ justifyContent: "end", paddingLeft: "0px" }}
      >
        {value}
      </Col>
    </Row>
    <hr style={{ width: "100%", margin: "4px 0px" }} />
  </>
);

const InfoRow = ({ label, value, paddingRight, infoContent, children }) => (
  <Row className={styles.Box1}>
    <Col
      xl={8}
      lg={8}
      md={6}
      sm={8}
      xs={8}
      className={styles.transactionCol}
      style={{ justifyContent: "start" }}
    >
      {label}
      {infoContent && (
        <>
          {"  "}
          <InfoBox content={infoContent} show={false} image={infoicon} />{" "}
        </>
      )}
    </Col>
    <Col
      xl={4}
      lg={4}
      md={6}
      sm={4}
      xs={4}
      className={styles.transactionCol}
      style={{
        justifyContent: "end",
        paddingRight: paddingRight,
        position: children ? "relative" : "static",
      }}
    >
      {value} {value !== DEFAULT_VALUE && "INR"}
      {children}
    </Col>
  </Row>
);

function CalculatorBox() {
  const [quoteData, setQuoteData] = useState(initialQuoteState);
  const [amount, setAmount] = useState("10000");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [paddingRight, setPaddingRight] = useState("10px");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { planId, partnerCode, themeColor } = useMyContext();
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const resetQuoteData = useCallback(() => {
    setQuoteData(initialQuoteState);
  }, []);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  }, []);

  const handleDropdown = useCallback(() => {
    setShowDropdown((prev) => !prev);
  }, []);

  const generateQuoteApi = useCallback(async () => {
    const body = {
      amount,
      fromCurrency: "INR",
      toCurrency: currency,
      inputCurrency: currency,
      panNo: "CVVPA1327A",
      planCd: planId,
      partnerCd: partnerCode,
    };

    try {
      const res = await generateDirectQuote(body);
      if (!res.data || amount === "") {
        resetQuoteData();
        return;
      }

      const { data } = res;
      const exRate = decimalAdjust("floor", data.exRate, -4);
      const convertingAmount = decimalAdjust("floor", amount * exRate, -1);
      const gstAmount = decimalAdjust("floor", data.gstAmt, -1);
      const tcsAmount = decimalAdjust("floor", data.tcsAmt, -1);
      const finalAmount = decimalAdjust("floor", data.finalFromAmount, -1);

      setQuoteData({
        transferFee: addCommasInPrice(
          data.totalBankCharges + data.handlecharge,
        ),
        amountConversion: addCommasInPrice(convertingAmount),
        forexRate: addCommasInPrice(exRate),
        gst: addCommasInPrice(gstAmount),
        tcs: addCommasInPrice(tcsAmount),
        totalAdditionalAmount: data.totalAdditionalAmount,
        finalAmount: addCommasInPrice(finalAmount),
      });
    } catch (error) {
      console.error("Error generating quote:", error);
      resetQuoteData();
    }
  }, [amount, currency, planId, partnerCode, resetQuoteData]);

  // Set padding for mobile screens
  useEffect(() => {
    if (window.innerWidth < 600) {
      setPaddingRight("0px");
    }
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // Generate quote when planId changes
  useEffect(() => {
    if (planId) generateQuoteApi();
  }, [planId, generateQuoteApi]);

  // Debounced quote generation on amount/currency change
  useEffect(() => {
    resetQuoteData();

    if (amount === "" || amount <= 0) return;

    const timeoutId = setTimeout(generateQuoteApi, DEBOUNCE_DELAY);
    return () => clearTimeout(timeoutId);
  }, [amount, currency, generateQuoteApi, resetQuoteData]);

  const {
    transferFee,
    amountConversion,
    finalAmount,
    tcs,
    gst,
    forexRate,
    totalAdditionalAmount,
  } = quoteData;

  return (
    <div className={styles.transactionBox}>
      <Row>
        <InputField
          label="Recipient Gets"
          conversionValueButton={defaultCurrency}
          value={amount}
          onChange={(e) => inputAmount(e, setAmount)}
          dropdown={true}
          dropdownType="HomeScreen"
          setCurrency={setCurrency}
        />
      </Row>

      <InfoRow
        label="Lowest Exchange Rate"
        value={forexRate}
        paddingRight={paddingRight}
        infoContent="Lowest Forex Markup for the currency as negotiated with our Authorised Dealer"
      />

      <hr className={styles.horizontalline} />

      <Row className={styles.Box2}>
        <Col
          xl={8}
          lg={8}
          md={6}
          sm={8}
          xs={8}
          className={styles.transactionCol}
          style={{ justifyContent: "start" }}
        >
          Total amount we'll convert
        </Col>
        <Col
          xl={4}
          lg={4}
          md={6}
          sm={4}
          xs={4}
          className={styles.transactionCol}
          style={{ justifyContent: "end", paddingRight: paddingRight }}
        >
          {amountConversion} {amountConversion !== DEFAULT_VALUE && "INR"}
        </Col>
      </Row>

      <hr className={styles.horizontalline} />

      <Row className={styles.Box1}>
        <Col
          xl={8}
          lg={8}
          md={6}
          sm={8}
          xs={8}
          className={styles.transactionCol}
          style={{ justifyContent: "start" }}
        >
          Est. Taxes & Fees
        </Col>
        <Col
          xl={4}
          lg={4}
          md={6}
          sm={4}
          xs={4}
          className={styles.transactionCol}
          style={{
            justifyContent: "end",
            position: "relative",
            paddingRight: paddingRight,
          }}
        >
          {totalAdditionalAmount}
          {totalAdditionalAmount !== DEFAULT_VALUE && (
            <>
              &nbsp; INR
              <img
                ref={dropdownRef}
                src={showDropdown ? upwaredicon : downwardicon}
                style={{
                  marginLeft: "10px",
                  height: "6px",
                  width: "12px",
                  cursor: "pointer",
                }}
                onClick={handleDropdown}
                alt=""
              />
            </>
          )}
          {showDropdown && (
            <div className={styles.dropdownDiv}>
              <Row>
                <Col
                  md={8}
                  sm={8}
                  xs={8}
                  className={styles.dropdownCol}
                  style={{ paddingRight: "0px" }}
                >
                  Our Fees
                </Col>
                <Col
                  md={4}
                  sm={4}
                  xs={4}
                  className={styles.dropdownblueCol}
                  style={{ paddingLeft: "0px" }}
                >
                  <s
                    className={styles.dropdownCol}
                    style={{ color: "black", marginRight: "4px" }}
                  >
                    500
                  </s>{" "}
                  0
                </Col>
              </Row>
              <hr style={{ width: "100%", margin: "4px 0px" }} />
              <DropdownRow label="Bank Transfer Fees" value={transferFee} />
              <DropdownRow label="TCS (Est.)" value={tcs} />
              <Row>
                <Col
                  sm={8}
                  xs={8}
                  className={styles.dropdownCol}
                  style={{ paddingRight: "0px" }}
                >
                  GST (Est.)
                </Col>
                <Col
                  md={4}
                  sm={4}
                  xs={4}
                  className={styles.dropdownCol}
                  style={{ justifyContent: "end", paddingLeft: "0px" }}
                >
                  {gst}
                </Col>
              </Row>
            </div>
          )}
        </Col>
      </Row>

      <hr className={styles.horizontalline} />

      <Row style={{ marginTop: "4px" }}>
        <InputField
          label="You Pay"
          conversionValueButton="INR"
          disabled={true}
          value={finalAmount}
          inputSize="home"
          image={indiaflag}
        />
      </Row>

      <Row className={styles.arrivetext}>
        <img src={arriveIcon} alt="" className={styles.arriveImage} />
        Should arrive in{" "}
        <strong style={{ width: "fit-content", padding: "0px 4px" }}>
          48 hours
        </strong>
      </Row>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          className={styles.getStartedButton}
          style={{ backgroundColor: themeColor, borderColor: themeColor }}
          onClick={() => navigate("/login")}
        >
          Get Started
        </Button>
      </div>

      <div className={styles.comparediv} onClick={() => setShowModal(true)}>
        How we compare
      </div>

      <SideModal showModal={showModal} setShowModal={setShowModal} />
    </div>
  );
}

export default CalculatorBox;
